import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2, ChevronDown, ChevronUp, FlaskConical } from 'lucide-react';
import { PageBody, PageHeader } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { reportsApi, type ProjectionData } from '@/api';
import { formatBRL } from '@/lib/utils';

type ReportType = 'projection' | 'cash-flow' | 'dre' | 'aging-payable' | 'aging-receivable';

export function RelatoriosPage() {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10);

  const [from, setFrom] = useState(firstDay);
  const [to, setTo] = useState(lastDay);
  const [type, setType] = useState<ReportType>('projection');
  const [year, setYear] = useState<number>(today.getFullYear());
  const [includeRecurring, setIncludeRecurring] = useState(false);

  const dre = useQuery({
    queryKey: ['report-dre', from, to],
    queryFn: () => reportsApi.dre(from, to),
    enabled: type === 'dre',
  });
  const cf = useQuery({
    queryKey: ['report-cf', from, to],
    queryFn: () => reportsApi.cashFlow(from, to),
    enabled: type === 'cash-flow',
  });
  const aging = useQuery({
    queryKey: ['report-aging', type],
    queryFn: () => reportsApi.aging(type === 'aging-payable' ? 'payable' : 'receivable'),
    enabled: type === 'aging-payable' || type === 'aging-receivable',
  });
  const projection = useQuery({
    queryKey: ['report-projection', year, includeRecurring],
    queryFn: () => reportsApi.projection(year, includeRecurring),
    enabled: type === 'projection',
  });

  return (
    <>
      <PageHeader title="Relatórios" breadcrumb="Análises" />
      <PageBody>
        <div className="bg-card border rounded-lg p-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Tipo</Label>
            <Select value={type} onChange={(e) => setType(e.target.value as ReportType)}>
              <option value="projection">Projeção de saldo (anual)</option>
              <option value="dre">DRE simplificado</option>
              <option value="cash-flow">Fluxo de caixa</option>
              <option value="aging-payable">Aging — A pagar</option>
              <option value="aging-receivable">Aging — A receber</option>
            </Select>
          </div>
          {type === 'projection' ? (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Ano</Label>
                <Select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                  {Array.from({ length: 7 }, (_, i) => today.getFullYear() - 3 + i).map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5 flex flex-col justify-end">
                <Label className="text-xs flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeRecurring}
                    onChange={(e) => setIncludeRecurring(e.target.checked)}
                    className="h-4 w-4"
                  />
                  Projetar contratos recorrentes
                </Label>
                <span className="text-[10px] text-muted-foreground leading-tight">
                  Quando ligado, soma o valor dos contratos recorrentes em meses futuros sem Payable/Receivable gerado.
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">De</Label>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Até</Label>
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
            </>
          )}
        </div>

        {type === 'projection' && projection.data && <ProjectionView data={projection.data} />}

        {type === 'dre' && dre.data && (
          <Card className="p-6 space-y-4">
            <div>
              <h3 className="font-medium">DRE Simplificado</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Regime de competência por vencimento. Soma o <span className="font-medium">amount cheio</span>{' '}
                de Receivables (receitas) e Payables (despesas) com dueDate no período, exceto cancelados.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3">
                <div className="text-xs text-emerald-800 uppercase">Receitas</div>
                <div className="text-xl font-medium text-emerald-900">{formatBRL(dre.data.revenue)}</div>
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded-md p-3">
                <div className="text-xs text-rose-800 uppercase">Despesas</div>
                <div className="text-xl font-medium text-rose-900">{formatBRL(dre.data.expense)}</div>
              </div>
              <div className={`rounded-md p-3 border ${Number(dre.data.result) >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                <div className="text-xs uppercase">Resultado</div>
                <div className={`text-xl font-medium ${Number(dre.data.result) >= 0 ? 'text-emerald-900' : 'text-rose-900'}`}>
                  {formatBRL(dre.data.result)}
                </div>
              </div>
            </div>
            {dre.data.breakdown.length > 0 && <DreBreakdown breakdown={dre.data.breakdown} />}
          </Card>
        )}

        {type === 'cash-flow' && cf.data && (
          <Card className="p-6 space-y-4">
            <div>
              <h3 className="font-medium">Fluxo de caixa por vencimento</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Soma o <span className="font-medium">amount cheio</span> dos Payables/Receivables pelo dia do
                vencimento. Saldo inicial = saldo real consolidado da org até o dia anterior ao período.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="border rounded-md p-3">
                <div className="text-xs text-muted-foreground uppercase">Saldo inicial</div>
                <div className="text-lg font-medium">{formatBRL(cf.data.initialBalance)}</div>
              </div>
              <div className="border border-emerald-200 bg-emerald-50/50 rounded-md p-3">
                <div className="text-xs text-emerald-700 uppercase">Entradas</div>
                <div className="text-lg font-medium text-emerald-900">{formatBRL(cf.data.totalIn)}</div>
              </div>
              <div className="border border-rose-200 bg-rose-50/50 rounded-md p-3">
                <div className="text-xs text-rose-700 uppercase">Saídas</div>
                <div className="text-lg font-medium text-rose-900">{formatBRL(cf.data.totalOut)}</div>
              </div>
              <div className={`border rounded-md p-3 ${cf.data.finalBalance >= 0 ? 'border-emerald-200 bg-emerald-50/50' : 'border-rose-200 bg-rose-50/50'}`}>
                <div className="text-xs uppercase text-muted-foreground">Saldo final</div>
                <div className={`text-lg font-medium ${cf.data.finalBalance >= 0 ? 'text-emerald-900' : 'text-rose-900'}`}>
                  {formatBRL(cf.data.finalBalance)}
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Data</th>
                    <th className="px-3 py-2 font-medium text-right">Entradas</th>
                    <th className="px-3 py-2 font-medium text-right">Saídas</th>
                    <th className="px-3 py-2 font-medium text-right">Saldo acumulado</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {cf.data.series.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                        Nenhum vencimento no período.
                      </td>
                    </tr>
                  )}
                  {cf.data.series.map((s) => (
                    <tr key={s.date}>
                      <td className="px-3 py-2">{formatBRDateKey(s.date)}</td>
                      <td className="px-3 py-2 text-right text-emerald-700">{formatBRL(s.in)}</td>
                      <td className="px-3 py-2 text-right text-rose-700">{formatBRL(s.out)}</td>
                      <td className={`px-3 py-2 text-right font-medium ${s.balance >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {formatBRL(s.balance)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {(type === 'aging-payable' || type === 'aging-receivable') && aging.data && (
          <Card className="p-6">
            <h3 className="font-medium mb-3">
              Aging — {type === 'aging-payable' ? 'Contas a pagar' : 'Contas a receber'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              {aging.data.map((b) => (
                <div key={b.label} className="border rounded-md p-3">
                  <div className="text-xs text-muted-foreground">{b.label}</div>
                  <div className="text-lg font-medium mt-1">{formatBRL(b.total)}</div>
                  <div className="text-xs text-muted-foreground mt-1">{b.count} títulos</div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </PageBody>
    </>
  );
}

// =====================================================================
// DRE — detalhamento por categoria (chartOfAccount)
// =====================================================================
type DreBreakdownItem = {
  chartOfAccount?: { id: string; code: string; name: string } | undefined;
  direction: 'IN' | 'OUT';
  total: string;
};

function DreBreakdown({ breakdown }: { breakdown: DreBreakdownItem[] }) {
  const ins = breakdown
    .filter((b) => b.direction === 'IN' && Number(b.total) > 0)
    .sort((a, b) => Number(b.total) - Number(a.total));
  const outs = breakdown
    .filter((b) => b.direction === 'OUT' && Number(b.total) > 0)
    .sort((a, b) => Number(b.total) - Number(a.total));
  const insTotal = ins.reduce((s, b) => s + Number(b.total), 0);
  const outsTotal = outs.reduce((s, b) => s + Number(b.total), 0);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t">
      <BreakdownColumn title="Receitas por categoria" items={ins} total={insTotal} accent="emerald" />
      <BreakdownColumn title="Despesas por categoria" items={outs} total={outsTotal} accent="rose" />
    </div>
  );
}

function BreakdownColumn({
  title, items, total, accent,
}: { title: string; items: DreBreakdownItem[]; total: number; accent: 'emerald' | 'rose' }) {
  const barColor = accent === 'emerald' ? 'bg-emerald-500' : 'bg-rose-500';
  const textColor = accent === 'emerald' ? 'text-emerald-800' : 'text-rose-800';
  return (
    <div>
      <div className="text-xs text-muted-foreground uppercase mb-2">{title}</div>
      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground italic">Sem lançamentos no período.</div>
      ) : (
        <div className="space-y-1.5 text-sm">
          {items.map((b, i) => {
            const v = Number(b.total);
            const pct = total > 0 ? (v / total) * 100 : 0;
            return (
              <div key={b.chartOfAccount?.id ?? i}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate">
                    <span className="text-muted-foreground text-xs mr-1">{b.chartOfAccount?.code}</span>
                    {b.chartOfAccount?.name ?? '—'}
                  </span>
                  <span className={`font-medium ${textColor} tabular-nums`}>{formatBRL(b.total)}</span>
                </div>
                <div className="h-1 bg-muted rounded-sm mt-1 overflow-hidden">
                  <div className={barColor} style={{ width: `${pct}%`, height: '100%' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Projeção de saldo — visão anual com gráfico de linha (saldo final),
// gráfico de barras (entradas vs saídas), tabela mensal e cards de
// fechamento por trimestre, semestre e ano.
//
// Simulação de cenários: o usuário pode adicionar ajustes hipotéticos
// (a receber/a pagar, valor, mês único ou recorrente). Os valores são
// aplicados em tempo real apenas no frontend — não tocam o banco.
// =====================================================================

type Simulation = {
  id: string;
  direction: 'IN' | 'OUT';
  description: string;
  amount: number; // sempre positivo
  // Faixa de meses 1-12 (inclusive). Para mês único basta startMonth=endMonth.
  startMonth: number;
  endMonth: number;
};

/** Aplica simulações sobre uma ProjectionData, retornando outra com totais
 *  recalculados (cada mês, trimestre, semestre, ano). */
function applySimulations(data: ProjectionData, sims: Simulation[]): ProjectionData {
  if (sims.length === 0) return data;

  const recv = data.months.map((m) => Number(m.received));
  const paid = data.months.map((m) => Number(m.paid));
  for (const s of sims) {
    const a = Math.max(0, Math.min(11, s.startMonth - 1));
    const b = Math.max(0, Math.min(11, s.endMonth - 1));
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    for (let i = lo; i <= hi; i += 1) {
      if (s.direction === 'IN') recv[i] += s.amount;
      else paid[i] += s.amount;
    }
  }

  let bal = Number(data.initialBalance);
  const months = data.months.map((m, i) => {
    const flow = recv[i] - paid[i];
    bal += flow;
    return {
      ...m,
      received: recv[i].toFixed(2),
      paid: paid[i].toFixed(2),
      flow: flow.toFixed(2),
      endingBalance: bal.toFixed(2),
    };
  });

  const aggregate = (label: string, from: number, to: number) => {
    let r = 0;
    let p = 0;
    for (let i = from; i <= to; i += 1) { r += recv[i]; p += paid[i]; }
    return {
      label,
      received: r.toFixed(2),
      paid: p.toFixed(2),
      flow: (r - p).toFixed(2),
      endingBalance: months[to].endingBalance,
    };
  };

  return {
    ...data,
    months,
    quarters: [aggregate('Q1', 0, 2), aggregate('Q2', 3, 5), aggregate('Q3', 6, 8), aggregate('Q4', 9, 11)],
    semesters: [aggregate('S1', 0, 5), aggregate('S2', 6, 11)],
    yearTotal: aggregate(String(data.year), 0, 11),
  };
}

function ProjectionView({ data: rawData }: { data: ProjectionData }) {
  const [simulations, setSimulations] = useState<Simulation[]>([]);
  const [simOpen, setSimOpen] = useState(false);

  const data = useMemo(() => applySimulations(rawData, simulations), [rawData, simulations]);
  const hasSim = simulations.length > 0;

  const balances = data.months.map((m) => Number(m.endingBalance));
  const baseBalances = rawData.months.map((m) => Number(m.endingBalance));
  const minBal = Math.min(0, ...balances, ...baseBalances);
  const maxBal = Math.max(0, ...balances, ...baseBalances);
  const totalReceived = data.yearTotal.received;
  const totalPaid = data.yearTotal.paid;
  const yearFlow = Number(data.yearTotal.flow);
  const finalBalance = data.months[11].endingBalance;
  const initialBalance = data.initialBalance;

  return (
    <div className="space-y-4">
      {/* Painel de simulação */}
      <SimulationPanel
        open={simOpen}
        onOpenChange={setSimOpen}
        simulations={simulations}
        onChange={setSimulations}
        year={data.year}
      />

      {/* Resumo do ano */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className={`p-4 ${hasSim ? 'border-blue-200 bg-blue-50/40' : ''}`}>
          <div className="text-xs text-muted-foreground uppercase">Saldo inicial</div>
          <div className="text-xl font-medium">{formatBRL(initialBalance)}</div>
          <div className="text-xs text-muted-foreground mt-1">31/12/{data.year - 1}</div>
        </Card>
        <Card className={`p-4 ${hasSim ? 'border-blue-200 bg-blue-50/40' : ''}`}>
          <div className="text-xs text-emerald-700 uppercase">Total entradas</div>
          <div className="text-xl font-medium text-emerald-800">{formatBRL(totalReceived)}</div>
          {hasSim ? (
            <div className="text-xs text-blue-700 mt-1">
              Real: {formatBRL(rawData.yearTotal.received)}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground mt-1">no ano</div>
          )}
        </Card>
        <Card className={`p-4 ${hasSim ? 'border-blue-200 bg-blue-50/40' : ''}`}>
          <div className="text-xs text-rose-700 uppercase">Total saídas</div>
          <div className="text-xl font-medium text-rose-800">{formatBRL(totalPaid)}</div>
          {hasSim ? (
            <div className="text-xs text-blue-700 mt-1">
              Real: {formatBRL(rawData.yearTotal.paid)}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground mt-1">no ano</div>
          )}
        </Card>
        <Card className={`p-4 border ${Number(finalBalance) >= 0 ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'} ${hasSim ? 'ring-1 ring-blue-300' : ''}`}>
          <div className="text-xs uppercase text-muted-foreground">
            Saldo final {data.year}{hasSim && ' (simulado)'}
          </div>
          <div className={`text-xl font-medium ${Number(finalBalance) >= 0 ? 'text-emerald-900' : 'text-rose-900'}`}>
            {formatBRL(finalBalance)}
          </div>
          {hasSim ? (
            <div className="text-xs text-blue-700 mt-1">
              Real: {formatBRL(rawData.months[11].endingBalance)}
            </div>
          ) : (
            <div className={`text-xs mt-1 ${yearFlow >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
              Resultado: {yearFlow >= 0 ? '+' : ''}{formatBRL(yearFlow)}
            </div>
          )}
        </Card>
      </div>

      {/* Gráfico de linha — saldo final mês a mês */}
      <Card className="p-6">
        <h3 className="font-medium mb-3">
          Projeção do saldo final — {data.year}
          {hasSim && <span className="ml-2 text-xs font-normal text-blue-700">(linha tracejada = projeção real sem simulação)</span>}
        </h3>
        <BalanceLineChart data={data} baseData={hasSim ? rawData : undefined} minBal={minBal} maxBal={maxBal} />
      </Card>

      {/* Gráfico de barras — entradas x saídas */}
      <Card className="p-6">
        <h3 className="font-medium mb-3">Entradas vs Saídas por mês</h3>
        <FlowBarChart data={data} />
      </Card>

      {/* Cards de fechamento — trimestral / semestral / anual */}
      <Card className="p-6 space-y-4">
        <h3 className="font-medium">Fechamento por período</h3>
        <div>
          <div className="text-xs text-muted-foreground uppercase mb-2">Trimestres</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {data.quarters.map((q) => (
              <PeriodCard key={q.label} agg={q} />
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground uppercase mb-2">Semestres</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data.semesters.map((s) => (
              <PeriodCard key={s.label} agg={s} />
            ))}
          </div>
        </div>
      </Card>

      {/* Tabela detalhada — mês × recebido / pago / fluxo / saldo */}
      <Card className="p-6">
        <h3 className="font-medium mb-1">Detalhamento mensal</h3>
        <p className="text-xs text-muted-foreground mb-3">
          Soma o <span className="font-medium">valor cheio (`amount`)</span> de cada Payable/Receivable pelo
          mês do vencimento (dueDate). Mesmo critério das telas Contas a Pagar / Contas a Receber.
          Cancelados e o pai agregador de parcelamentos são excluídos.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Mês</th>
                <th className="px-3 py-2 font-medium text-right">Entradas</th>
                <th className="px-3 py-2 font-medium text-right">Saídas</th>
                <th className="px-3 py-2 font-medium text-right">Fluxo</th>
                <th className="px-3 py-2 font-medium text-right">Saldo final</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              <tr className="bg-muted/20">
                <td className="px-3 py-2 text-xs text-muted-foreground italic" colSpan={4}>
                  Saldo inicial em 31/12/{data.year - 1}
                </td>
                <td className="px-3 py-2 text-right font-medium">{formatBRL(initialBalance)}</td>
              </tr>
              {data.months.map((m, i) => {
                const flow = Number(m.flow);
                const bal = Number(m.endingBalance);
                const baseRecv = Number(rawData.months[i].received);
                const basePaid = Number(rawData.months[i].paid);
                const recvDiff = Number(m.received) - baseRecv;
                const paidDiff = Number(m.paid) - basePaid;
                const rowAffected = hasSim && (recvDiff !== 0 || paidDiff !== 0);
                return (
                  <tr key={m.month} className={rowAffected ? 'bg-blue-50/40' : ''}>
                    <td className="px-3 py-2 font-medium">{m.label}/{data.year}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">
                      {formatBRL(m.received)}
                      {recvDiff !== 0 && (
                        <span className="text-[10px] text-blue-700 ml-1">(+{formatBRL(recvDiff)})</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-rose-700">
                      {formatBRL(m.paid)}
                      {paidDiff !== 0 && (
                        <span className="text-[10px] text-blue-700 ml-1">(+{formatBRL(paidDiff)})</span>
                      )}
                    </td>
                    <td className={`px-3 py-2 text-right ${flow >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {flow >= 0 ? '+' : ''}{formatBRL(m.flow)}
                    </td>
                    <td className={`px-3 py-2 text-right font-medium ${bal >= 0 ? 'text-emerald-800' : 'text-rose-800'}`}>
                      {formatBRL(m.endingBalance)}
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-muted/30 font-medium">
                <td className="px-3 py-2">Total {data.year}</td>
                <td className="px-3 py-2 text-right text-emerald-800">{formatBRL(totalReceived)}</td>
                <td className="px-3 py-2 text-right text-rose-800">{formatBRL(totalPaid)}</td>
                <td className={`px-3 py-2 text-right ${yearFlow >= 0 ? 'text-emerald-800' : 'text-rose-800'}`}>
                  {yearFlow >= 0 ? '+' : ''}{formatBRL(data.yearTotal.flow)}
                </td>
                <td className={`px-3 py-2 text-right ${Number(finalBalance) >= 0 ? 'text-emerald-900' : 'text-rose-900'}`}>
                  {formatBRL(finalBalance)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function PeriodCard({ agg }: { agg: ProjectionData['quarters'][number] }) {
  const flow = Number(agg.flow);
  const bal = Number(agg.endingBalance);
  return (
    <div className={`border rounded-md p-3 ${bal >= 0 ? 'border-emerald-200 bg-emerald-50/40' : 'border-rose-200 bg-rose-50/40'}`}>
      <div className="text-xs uppercase text-muted-foreground">{agg.label}</div>
      <div className="grid grid-cols-2 gap-2 mt-1 text-xs">
        <div>
          <div className="text-muted-foreground">Entradas</div>
          <div className="text-emerald-700 font-medium">{formatBRL(agg.received)}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Saídas</div>
          <div className="text-rose-700 font-medium">{formatBRL(agg.paid)}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Resultado</div>
          <div className={`font-medium ${flow >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
            {flow >= 0 ? '+' : ''}{formatBRL(agg.flow)}
          </div>
        </div>
        <div>
          <div className="text-muted-foreground">Saldo final</div>
          <div className={`font-medium ${bal >= 0 ? 'text-emerald-900' : 'text-rose-900'}`}>
            {formatBRL(agg.endingBalance)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Gráfico de linha (saldo final por mês) ---------------------------
function BalanceLineChart({
  data, baseData, minBal, maxBal,
}: { data: ProjectionData; baseData?: ProjectionData; minBal: number; maxBal: number }) {
  const W = 760;
  const H = 280;
  const padL = 64;
  const padR = 16;
  const padT = 16;
  const padB = 36;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const range = Math.max(maxBal - minBal, 1);
  const yScale = (v: number) => padT + innerH - ((v - minBal) / range) * innerH;
  const xScale = (i: number) => padL + (i / 11) * innerW;
  const zeroY = yScale(0);

  const points = data.months.map((m, i) => ({
    x: xScale(i),
    y: yScale(Number(m.endingBalance)),
    month: m,
  }));
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

  const ticks = useMemo(() => {
    const out: number[] = [];
    const step = range / 4;
    for (let i = 0; i <= 4; i += 1) out.push(minBal + step * i);
    return out;
  }, [minBal, range]);

  const [hover, setHover] = useState<number | null>(null);

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-full" style={{ minWidth: 600 }}>
        {/* grid horizontal + labels Y */}
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padL} x2={W - padR} y1={yScale(t)} y2={yScale(t)} stroke="currentColor" strokeOpacity={0.08} />
            <text x={padL - 8} y={yScale(t) + 4} fontSize={10} textAnchor="end" fill="currentColor" opacity={0.6}>
              {abbrevBRL(t)}
            </text>
          </g>
        ))}
        {/* eixo X — linha do zero destacada */}
        {minBal < 0 && maxBal > 0 && (
          <line x1={padL} x2={W - padR} y1={zeroY} y2={zeroY} stroke="currentColor" strokeOpacity={0.4} strokeDasharray="4 3" />
        )}
        {/* labels mês */}
        {points.map((p, i) => (
          <text key={i} x={p.x} y={H - padB + 16} fontSize={11} textAnchor="middle" fill="currentColor" opacity={0.7}>
            {data.months[i].label}
          </text>
        ))}
        {/* linha base (real, sem simulação) — tracejada, atrás */}
        {baseData && (
          <path
            d={baseData.months
              .map((m, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(Number(m.endingBalance))}`)
              .join(' ')}
            fill="none"
            stroke="rgb(100 116 139)"
            strokeWidth={1.5}
            strokeDasharray="5 4"
            opacity={0.7}
          />
        )}
        {/* linha do saldo (simulada se houver simulação, senão real) */}
        <path d={pathD} fill="none" stroke={baseData ? 'rgb(37 99 235)' : 'rgb(16 185 129)'} strokeWidth={2.5} />
        {/* pontos + áreas de hover */}
        {points.map((p, i) => {
          const negative = Number(data.months[i].endingBalance) < 0;
          const dotColor = baseData
            ? (negative ? 'rgb(225 29 72)' : 'rgb(37 99 235)')
            : (negative ? 'rgb(225 29 72)' : 'rgb(16 185 129)');
          return (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r={hover === i ? 6 : 4} fill={dotColor} stroke="white" strokeWidth={2} />
              {/* hitbox */}
              <rect
                x={p.x - innerW / 24}
                y={padT}
                width={innerW / 12}
                height={innerH}
                fill="transparent"
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              />
            </g>
          );
        })}
        {/* tooltip */}
        {hover !== null && (() => {
          const m = data.months[hover];
          const p = points[hover];
          const tipW = 168;
          const tipH = 88;
          const tipX = Math.min(W - padR - tipW, Math.max(padL, p.x - tipW / 2));
          const tipY = Math.max(padT, p.y - tipH - 12);
          const negative = Number(m.endingBalance) < 0;
          return (
            <g pointerEvents="none">
              <rect x={tipX} y={tipY} width={tipW} height={tipH} rx={6} fill="white" stroke="rgb(0 0 0 / 0.12)" />
              <text x={tipX + 10} y={tipY + 18} fontSize={12} fontWeight={600} fill="currentColor">
                {m.label}/{data.year}
              </text>
              <text x={tipX + 10} y={tipY + 36} fontSize={11} fill="rgb(4 120 87)">
                Entradas: {formatBRL(m.received)}
              </text>
              <text x={tipX + 10} y={tipY + 52} fontSize={11} fill="rgb(190 18 60)">
                Saídas: {formatBRL(m.paid)}
              </text>
              <text x={tipX + 10} y={tipY + 70} fontSize={12} fontWeight={600} fill={negative ? 'rgb(190 18 60)' : 'rgb(4 120 87)'}>
                Saldo: {formatBRL(m.endingBalance)}
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}

// ---- Gráfico de barras (entradas x saídas) ----------------------------
function FlowBarChart({ data }: { data: ProjectionData }) {
  const W = 760;
  const H = 240;
  const padL = 64;
  const padR = 16;
  const padT = 12;
  const padB = 32;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const maxV = Math.max(
    1,
    ...data.months.flatMap((m) => [Number(m.received), Number(m.paid)]),
  );
  const yScale = (v: number) => padT + innerH - (v / maxV) * innerH;
  const groupW = innerW / 12;
  const barW = (groupW - 6) / 2;
  const [hover, setHover] = useState<number | null>(null);

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-full" style={{ minWidth: 600 }}>
        {/* grid horizontal */}
        {[0, 0.25, 0.5, 0.75, 1].map((p) => {
          const v = maxV * p;
          return (
            <g key={p}>
              <line x1={padL} x2={W - padR} y1={yScale(v)} y2={yScale(v)} stroke="currentColor" strokeOpacity={0.08} />
              <text x={padL - 8} y={yScale(v) + 4} fontSize={10} textAnchor="end" fill="currentColor" opacity={0.6}>
                {abbrevBRL(v)}
              </text>
            </g>
          );
        })}
        {data.months.map((m, i) => {
          const groupX = padL + i * groupW + 3;
          const inV = Number(m.received);
          const outV = Number(m.paid);
          return (
            <g key={i}
               onMouseEnter={() => setHover(i)}
               onMouseLeave={() => setHover(null)}>
              <rect x={groupX} y={yScale(inV)} width={barW} height={Math.max(0, padT + innerH - yScale(inV))} fill="rgb(16 185 129)" opacity={hover === null || hover === i ? 1 : 0.4} />
              <rect x={groupX + barW + 2} y={yScale(outV)} width={barW} height={Math.max(0, padT + innerH - yScale(outV))} fill="rgb(225 29 72)" opacity={hover === null || hover === i ? 1 : 0.4} />
              <text x={groupX + barW + 1} y={H - padB + 14} fontSize={11} textAnchor="middle" fill="currentColor" opacity={0.7}>
                {m.label}
              </text>
              {/* hitbox */}
              <rect x={padL + i * groupW} y={padT} width={groupW} height={innerH} fill="transparent" />
            </g>
          );
        })}
        {/* legend */}
        <g transform={`translate(${padL}, ${padT - 4})`}>
          <rect width={10} height={10} fill="rgb(16 185 129)" />
          <text x={14} y={9} fontSize={11} fill="currentColor" opacity={0.8}>Entradas</text>
          <rect x={80} width={10} height={10} fill="rgb(225 29 72)" />
          <text x={94} y={9} fontSize={11} fill="currentColor" opacity={0.8}>Saídas</text>
        </g>
        {/* tooltip */}
        {hover !== null && (() => {
          const m = data.months[hover];
          const groupX = padL + hover * groupW;
          const tipW = 168;
          const tipH = 64;
          const tipX = Math.min(W - padR - tipW, Math.max(padL, groupX + groupW / 2 - tipW / 2));
          const tipY = padT + 4;
          return (
            <g pointerEvents="none">
              <rect x={tipX} y={tipY} width={tipW} height={tipH} rx={6} fill="white" stroke="rgb(0 0 0 / 0.12)" />
              <text x={tipX + 10} y={tipY + 18} fontSize={12} fontWeight={600} fill="currentColor">
                {m.label}/{data.year}
              </text>
              <text x={tipX + 10} y={tipY + 34} fontSize={11} fill="rgb(4 120 87)">
                Entradas: {formatBRL(m.received)}
              </text>
              <text x={tipX + 10} y={tipY + 50} fontSize={11} fill="rgb(190 18 60)">
                Saídas: {formatBRL(m.paid)}
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}

// =====================================================================
// Painel de simulação — adicionar/remover cenários hipotéticos
// =====================================================================
const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function SimulationPanel({
  open, onOpenChange, simulations, onChange, year,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  simulations: Simulation[];
  onChange: (sims: Simulation[]) => void;
  year: number;
}) {
  const [direction, setDirection] = useState<'IN' | 'OUT'>('IN');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState<'monthly' | 'once'>('monthly');
  const [startMonth, setStartMonth] = useState(1);
  const [endMonth, setEndMonth] = useState(12);
  const [singleMonth, setSingleMonth] = useState(new Date().getMonth() + 1);

  const total = simulations.reduce(
    (acc, s) => {
      const months = Math.abs(s.endMonth - s.startMonth) + 1;
      const v = s.amount * months;
      if (s.direction === 'IN') acc.recv += v;
      else acc.paid += v;
      return acc;
    },
    { recv: 0, paid: 0 },
  );

  const canAdd = description.trim().length > 0 && Number(amount) > 0;

  const add = () => {
    const v = Number(amount);
    if (!canAdd) return;
    const s: Simulation = {
      id: Math.random().toString(36).slice(2, 10),
      direction,
      description: description.trim(),
      amount: v,
      startMonth: mode === 'once' ? singleMonth : startMonth,
      endMonth: mode === 'once' ? singleMonth : endMonth,
    };
    onChange([...simulations, s]);
    setDescription('');
    setAmount('');
  };

  const remove = (id: string) => onChange(simulations.filter((s) => s.id !== id));
  const clear = () => onChange([]);

  return (
    <Card className={`p-4 ${simulations.length > 0 ? 'border-blue-300 bg-blue-50/30' : ''}`}>
      <button
        type="button"
        className="flex items-center justify-between w-full text-left"
        onClick={() => onOpenChange(!open)}
      >
        <div className="flex items-center gap-2">
          <FlaskConical className="h-4 w-4 text-blue-700" />
          <span className="font-medium">Simulação de cenários</span>
          {simulations.length > 0 && (
            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
              {simulations.length} ativa{simulations.length > 1 ? 's' : ''} · +{formatBRL(total.recv)} entradas / +{formatBRL(total.paid)} saídas
            </span>
          )}
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          <p className="text-xs text-muted-foreground">
            Adicione ajustes hipotéticos para ver como ficariam os saldos do ano. Não altera o banco —
            é apenas uma projeção visual. Útil para responder perguntas do tipo:
            <em> "se entrar mais um contrato de R$ 1.000 a partir de junho, como fica o saldo final?"</em>
          </p>

          {/* Lista de simulações ativas */}
          {simulations.length > 0 && (
            <div className="space-y-1.5">
              {simulations.map((s) => (
                <div key={s.id} className="flex items-center gap-2 text-sm bg-white border rounded-md px-3 py-2">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${s.direction === 'IN' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                    {s.direction === 'IN' ? 'Entrada' : 'Saída'}
                  </span>
                  <span className="flex-1 truncate">{s.description}</span>
                  <span className="font-medium tabular-nums">{formatBRL(s.amount)}/mês</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {s.startMonth === s.endMonth
                      ? `${MONTH_LABELS[s.startMonth - 1]}/${year}`
                      : `${MONTH_LABELS[s.startMonth - 1]}–${MONTH_LABELS[s.endMonth - 1]}`}
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => remove(s.id)} aria-label="Remover">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <div className="text-right">
                <Button size="sm" variant="ghost" onClick={clear}>Limpar todas</Button>
              </div>
            </div>
          )}

          {/* Form de adição */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-2 space-y-1.5">
              <Label className="text-xs">Tipo</Label>
              <Select value={direction} onChange={(e) => setDirection(e.target.value as 'IN' | 'OUT')}>
                <option value="IN">A receber</option>
                <option value="OUT">A pagar</option>
              </Select>
            </div>
            <div className="md:col-span-4 space-y-1.5">
              <Label className="text-xs">Descrição</Label>
              <Input
                placeholder="ex.: novo contrato de TI"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label className="text-xs">Valor mensal</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0,00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label className="text-xs">Recorrência</Label>
              <Select value={mode} onChange={(e) => setMode(e.target.value as 'monthly' | 'once')}>
                <option value="monthly">Mensal</option>
                <option value="once">Mês único</option>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Button onClick={add} disabled={!canAdd} className="w-full">
                <Plus className="h-4 w-4 mr-1" /> Adicionar
              </Button>
            </div>

            {mode === 'monthly' ? (
              <>
                <div className="md:col-span-3 md:col-start-3 space-y-1.5">
                  <Label className="text-xs">Mês inicial</Label>
                  <Select value={startMonth} onChange={(e) => setStartMonth(Number(e.target.value))}>
                    {MONTH_LABELS.map((m, i) => (
                      <option key={m} value={i + 1}>{m}/{year}</option>
                    ))}
                  </Select>
                </div>
                <div className="md:col-span-3 space-y-1.5">
                  <Label className="text-xs">Mês final</Label>
                  <Select value={endMonth} onChange={(e) => setEndMonth(Number(e.target.value))}>
                    {MONTH_LABELS.map((m, i) => (
                      <option key={m} value={i + 1}>{m}/{year}</option>
                    ))}
                  </Select>
                </div>
              </>
            ) : (
              <div className="md:col-span-3 md:col-start-3 space-y-1.5">
                <Label className="text-xs">Mês</Label>
                <Select value={singleMonth} onChange={(e) => setSingleMonth(Number(e.target.value))}>
                  {MONTH_LABELS.map((m, i) => (
                    <option key={m} value={i + 1}>{m}/{year}</option>
                  ))}
                </Select>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// Formata uma chave YYYY-MM-DD (já no fuso BR) como DD/MM/YYYY,
// sem passar por `new Date(...)` — porque o construtor interpreta strings
// YYYY-MM-DD como UTC e voltaria 1 dia em fusos negativos.
function formatBRDateKey(key: string): string {
  const [y, m, d] = key.split('-');
  return `${d}/${m}/${y}`;
}

// Abreviação compacta para eixos: 1.2k, 35k, 1.5M
function abbrevBRL(v: number): string {
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  return `${sign}${abs.toFixed(0)}`;
}
